import { useState } from "react";
import { useV1FormsFormFamiliesList, v1FormsFormFamiliesList } from "@/api/generated/v1/v1";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";
import { PageBanner } from "@/shared/components/PageBanner";

export function FormFamilyListPage() {
  const navigate = useNavigate();
  const canCreate = usePermission("forms.manage");

  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const queryParams = { page: 1, ...(searchTerm && { search: searchTerm }), ...activeFilters };
  const { data, isLoading, isError, error } = useV1FormsFormFamiliesList(queryParams);

  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [name]: value }));
  };

  const columns = [
    { header: "کد فرم", accessor: (row: any) => row.code },
    { header: "نام فرم", accessor: (row: any) => row.name },
  ];

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1FormsFormFamiliesList({
        export: "true",
        ...(searchTerm && { search: searchTerm }),
        ...activeFilters,
      } as any);
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "form_families_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "form_families_export");
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="خانواده‌های فرم"
        subtitle="مدیریت قالب‌ها و ساختار فرم‌های پویا"
        icon="📋"
        helpLink="/help/forms/families"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canCreate && (
            <button
              onClick={() => navigate("/forms/families/create")}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>ایجاد خانوادهٔ فرم</span>
            </button>
          )}
          <button
            onClick={() => navigate("/forms")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        <DataTableToolbar
          onSearch={setSearchTerm}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          filters={[]}
        />

        {isLoading && (
          <div className="flex-center p-8">
            <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری اطلاعات...</p>
          </div>
        )}

        {isError && (
          <div className="p-4 mb-4" style={{ background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red", borderRadius: "8px" }}>
            <p style={{ color: "red" }}>خطا در دریافت اطلاعات: {(error as Error)?.message}</p>
          </div>
        )}

        {!isLoading && !isError && data?.results && (
          <div className="table-container" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>کد فرم</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>نام فرم</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {data.results.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      هیچ خانوادهٔ فرمی یافت نشد.
                    </td>
                  </tr>
                ) : (
                  data.results.map((item: any) => (
                    <tr key={item.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "1rem" }} dir="ltr" className="text-left font-mono">
                        {item.code}
                      </td>
                      <td style={{ padding: "1rem", fontWeight: "bold" }}>{item.name}</td>
                      <td style={{ padding: "1rem" }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "0.85rem", padding: "0.3rem 0.75rem" }}
                          onClick={() => navigate(`/forms/families/${item.id}`)}
                        >
                          مشاهده و ویرایش
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {data?.count !== undefined && (
          <div className="mt-4 pt-4 border-t flex-between" style={{ borderColor: "var(--glass-border)", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            <span>تعداد کل: {data.count}</span>
            <span>صفحه ۱</span>
          </div>
        )}
      </div>
    </div>
  );
}
